/* structureRef.js
 *
 * A pragmatic structure key ("structureKey") that strips the session-specific
 * ` mountN` suffix from a MapCase uri (computed as MapCase.splitUri(uri).projectCase).
 * For server cases it is `project/case`; for scheme imports it is the bare scheme uri
 * (`pdb:4YZU`, `uniprot:...`). This is THE structure-identity mechanism.
 *
 * What it IS good for: collapsing the per-connection mount index so the same
 * loaded structure matches itself across loads/reloads within a session. That is
 * all the current callers need — curated kinase panels (server `project/case`
 * cases), within-session dedup / React keys / group-member identity, and matching
 * alignment reference frames (alignReferenceFrame stores a structureKey).
 *
 * What it is NOT: a durable, content-based structure identity. User uploads and
 * folded structures get session-local generated uris (`userdata:N` / `folding:N`,
 * from LocalIdManager) with no content in them, so their structureKey changes
 * between sessions and between re-imports of the same file. Do not rely on
 * structureKey to match uploaded or folded structures across save/restore or
 * re-import. (Server cases and pdb:/uniprot: imports are content-derived and do
 * round-trip.)
 *
 * GOTCHA: do NOT use mapCase.projectCase (the instance field) as the key — it is
 * '' for pdb:/uniprot: imports (see MapCase constructor), so every import would
 * collide on the empty string. structureKey returns the whole scheme uri instead.
 *
 * Tests in test/bmaps/structureRef.test.js.
 */
import { MapCase } from './MapCase';

/**
 * The mount-free structure identifier for a MapCase, a lightweight { uri } object, or a
 * raw uri string: the uri with any ` mountN` suffix stripped (via MapCase.splitUri). The
 * single source of truth for structure identity.
 * @param {MapCase|{uri: string}|string} mapCaseOrUri
 * @returns {string?} the structureKey, or null for empty/invalid input
 */
export function structureKey(mapCaseOrUri) {
    if (!mapCaseOrUri) return null;
    const uri = typeof mapCaseOrUri === 'string' ? mapCaseOrUri : mapCaseOrUri.uri;
    return uri ? MapCase.splitUri(uri).projectCase : null;
}

/**
 * Mount-free structure equality. Two arguments refer to the same structure when
 * their structureKeys are equal (and defined). A null key never matches anything.
 * @param {MapCase|{uri: string}|string} a
 * @param {MapCase|{uri: string}|string} b
 * @returns {boolean}
 */
export function sameStructure(a, b) {
    const keyA = structureKey(a);
    return keyA != null && keyA === structureKey(b);
}

/**
 * Inverse lookup: find the loaded MapCase whose structureKey matches `key`.
 * @param {string?} key a structureKey
 * @param {MapCase[]} mapCases the candidates to search (e.g. loaded structures)
 * @returns {MapCase?} the matching MapCase, or null
 */
export function resolveLoadedByKey(key, mapCases) {
    if (key == null || !mapCases) return null;
    return mapCases.find((mc) => structureKey(mc) === key) ?? null;
}

/**
 * A display label that disambiguates structures which share a molecule/PDB name --
 * e.g. curated kinase-panel members from different project-cases (EGFR/2ITY_altA vs
 * EGFR/2ITY_D): the MapCase's long name plus its structureKey (project/case) when
 * that adds information. Used by the simulation structure pickers/readiness rows and
 * the protein dropdown so a structure reads the same everywhere.
 * @param {MapCase} mapCase
 * @returns {string}
 */
export function structureLabel(mapCase) {
    if (!mapCase) return '';
    const longName = mapCase.getLongName?.() ?? '';
    const key = structureKey(mapCase);
    if (key && longName && !longName.includes(key)) return `${longName} — ${key}`;
    return longName || key || '';
}

/**
 * Disambiguated display labels for a set of structures shown together. Each label is
 * the structure's long name, suffixed with its project/case (via structureLabel) ONLY
 * when another structure in the set shares that long name -- e.g. two cases of one
 * kinase. Uniquely-named structures read as just their long name, so the common case
 * is uncluttered. Returns a Map keyed by uri: look up labels.get(mapCase.uri).
 * @param {MapCase[]} mapCases
 * @returns {Map<string, string>}
 */
export function structureLabelsByUri(mapCases) {
    const list = mapCases ?? [];
    const nameCounts = new Map();
    for (const mc of list) {
        if (!mc) continue;
        const name = mc.getLongName?.() ?? '';
        nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }
    const labels = new Map();
    for (const mc of list) {
        if (!mc) continue;
        const name = mc.getLongName?.() ?? '';
        const ambiguous = (nameCounts.get(name) ?? 0) > 1;
        labels.set(mc.uri, ambiguous ? structureLabel(mc) : (name || structureKey(mc) || ''));
    }
    return labels;
}
